import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import Table from "../../../components/table";
import Alert from "../../../components/alert";
import Badge from "../../../components/badge";
import Button from "../../../components/button";
import { SearchBox, Select } from "../../../components/input-functions";
import useScreenName from "../../../hooks/useScreenName";
import useQueryParams from "../../../hooks/useQueryParams";
import { dateToDDMMYYYYHHMMAP, splitKeyWithSpace } from "../../../utils";
import { RoutePaths } from "../../../models";
import dispatch, { ForbiddenError } from "../../../middleware";
import { listAuditLogs } from "../../../actions/credit-decision.action";
import DateTimeFilter, {
  getDateTimeRangeQuery,
  DateTimeRange,
} from "../../../components/date-time-filter";
import {
  CreditDecisionProductTypeEnum,
  AuditLogListQuery,
  CreditDecisionAuditLog,
} from "../../../models/creditDecision";

const columns = [
  { field: "id", label: "ID" },
  { field: "productType", label: "Product" },
  { field: "purpose", label: "Purpose" },
  { field: "user", label: "User" },
  { field: "employer", label: "Employer" },
  { field: "approved", label: "Approved" },
  { field: "evaluatedAt", label: "Evaluated At" },
];

export default function AuditLogsPage() {
  useScreenName("Audit Logs");
  const history = useHistory();
  const storeDispatch = useDispatch();
  const [queryParams, setQueryParams] = useQueryParams(
    { clientApplicationId: "", evaluatedAt: "" },
    { historyAction: "replace" }
  );

  const evaluatedAtParam = String(queryParams.evaluatedAt ?? "");

  const [productType, setProductType] = useState<CreditDecisionProductTypeEnum>(
    CreditDecisionProductTypeEnum.SALARY_TOPUP
  );
  const [approved, setApproved] = useState("");
  const [searchId, setSearchId] = useState("");
  const [employerId, setEmployerId] = useState("");
  const [workerId, setWorkerId] = useState("");
  const [clientApplicationId, setClientApplicationId] = useState(
    String(queryParams.clientApplicationId ?? "")
  );
  const [clientUserId, setClientUserId] = useState("");
  const [clientEmployerId, setClientEmployerId] = useState("");
  const [clientWorkerId, setClientWorkerId] = useState("");
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [dateTimeRange, setDateTimeRange] = useState<DateTimeRange>({
    filter: evaluatedAtParam === "all" ? "" : "last_30_minutes",
    from: "",
    to: "",
  });
  const [data, setData] = useState<CreditDecisionAuditLog[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notAuthorized, setNotAuthorized] = useState(false);

  const buildQuery = useCallback((): AuditLogListQuery => {
    const q: AuditLogListQuery = { productType };
    if (approved) q.approved = approved;
    if (searchId) q.userId = searchId;
    if (employerId) q.employerId = employerId;
    if (workerId) q.workerId = workerId;
    if (clientApplicationId) q.clientApplicationId = clientApplicationId;
    // if (clientUserId) q.clientUserId = clientUserId;
    // if (clientEmployerId) q.clientEmployerId = clientEmployerId;
    // if (clientWorkerId) q.clientWorkerId = clientWorkerId;
    const dateTimeQuery = getDateTimeRangeQuery(dateTimeRange);
    // When searching by clientApplicationId, skip the evaluatedAt time
    // filter so the entire history is scanned (full time scan).
    if (!clientApplicationId) {
      if (dateTimeQuery.evaluatedAtFrom)
        q.evaluatedAtFrom = dateTimeQuery.evaluatedAtFrom;
      if (dateTimeQuery.evaluatedAtTo)
        q.evaluatedAtTo = dateTimeQuery.evaluatedAtTo;
    }
    if (cursor) q.cursor = cursor;
    q.limit = "20";
    return q;
  }, [
    productType,
    approved,
    searchId,
    employerId,
    workerId,
    clientApplicationId,
    // clientUserId,
    // clientEmployerId,
    // clientWorkerId,
    dateTimeRange,
    cursor,
  ]);

  const fetchList = useCallback(
    async (resetCursor?: boolean) => {
      try {
        setIsLoading(true);
        const query = buildQuery();
        if (resetCursor) {
          delete query.cursor;
          setCursor(undefined);
        }
        const response = await dispatch(storeDispatch, listAuditLogs(query));
        if (resetCursor) {
          setData(response.data);
        } else {
          setData((prev) => [...prev, ...response.data]);
        }
        setCursor(response.nextCursor);
        setHasMore(response.hasMore);
      } catch (error) {
        if (error instanceof ForbiddenError) {
          setNotAuthorized(true);
        } else {
          console.error("Error fetching audit logs:", error);
          alert("Failed to fetch audit logs");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [buildQuery, storeDispatch]
  );

  useEffect(() => {
    fetchList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    productType,
    approved,
    employerId,
    workerId,
    clientApplicationId,
    // clientUserId,
    // clientEmployerId,
    // clientWorkerId,
    searchId,
    dateTimeRange,
  ]);

  function handleViewDetail(logId: string) {
    history.push(
      RoutePaths.CREDIT_DECISION_AUDIT_LOG_DETAIL.replace(":id", logId)
    );
  }

  function handleLoadMore() {
    if (hasMore && !isLoading) {
      fetchList(false);
    }
  }

  function handleClientApplicationSubmit(value: string) {
    setClientApplicationId(value);
    setQueryParams((prev) => ({ ...prev, clientApplicationId: value }));
  }

  function handleDateTimeRangeChange(nextRange: DateTimeRange) {
    setDateTimeRange(nextRange);
    if (queryParams.evaluatedAt === "all") {
      setQueryParams((prev) => {
        return { ...prev, evaluatedAt: "" };
      });
    }
  }

  function getTableData() {
    return data.map((row) => ({
      id: (
        <Button
          variant="blue-text"
          size="xs"
          onClick={() => handleViewDetail(row._id)}
        >
          {row._id}
        </Button>
      ),
      productType: splitKeyWithSpace(row.productType),
      purpose: splitKeyWithSpace(row.purpose),
      user: row.userId || row.clientReferences?.clientUserId || "-",
      employer: row.employerId || row.clientReferences?.clientEmployerId || "-",
      approved: (
        <Badge
          type={row.approved ? "success" : "reject"}
          capitalize
          style={{
            display: "inline-block",
            width: "auto",
            padding: "6px 10px",
          }}
        >
          {row.approved ? "Yes" : "No"}
        </Badge>
      ),
      evaluatedAt: row.evaluatedAt
        ? dateToDDMMYYYYHHMMAP(row.evaluatedAt)
        : "-",
    }));
  }

  return (
    <div>
      <h2 className="mb-4">Credit Decision Audit Logs</h2>
      {notAuthorized ? (
        <Alert type="danger" message="Not authorized to view audit logs." />
      ) : (
      <div className="dashboard-container">
        <div className="mb-4">
          <div className="row g-3 align-items-end audit-log-filters__top-row">
            <div className="col-12 col-md-4 col-lg-3 audit-log-filters__field">
              <label className="form-label">Product Type *</label>
              <Select
                className="w-100"
                value={productType}
                onChange={(e) =>
                  setProductType(
                    e.target.value as CreditDecisionProductTypeEnum
                  )
                }
                options={[
                  {
                    value: CreditDecisionProductTypeEnum.SALARY_TOPUP,
                    label: "Salary Topup",
                  },
                  { value: CreditDecisionProductTypeEnum.EWA, label: "EWA" },
                ]}
              />
            </div>
            <div className="col-12 col-md-4 col-lg-3 audit-log-filters__field">
              <label className="form-label">Approved</label>
              <Select
                className="w-100"
                value={approved}
                onChange={(e) => setApproved(e.target.value)}
                options={[
                  { value: "", label: "All" },
                  { value: "true", label: "Yes" },
                  { value: "false", label: "No" },
                ]}
              />
            </div>
            <DateTimeFilter
              label="Evaluated At"
              value={dateTimeRange}
              onChange={handleDateTimeRangeChange}
            />
          </div>
          <div className="audit-log-filters__toggle-row mt-3">
            <Button
              type="button"
              variant="text"
              className="audit-log-filters__toggle p-0"
              onClick={() => setShowMoreFilters((prev) => !prev)}
              aria-expanded={showMoreFilters}
            >
              {showMoreFilters
                ? "Hide additional filters"
                : "Show additional filters"}
            </Button>
          </div>
          <div
            className={`audit-log-filters__more ${
              showMoreFilters ? "is-open" : ""
            }`}
          >
            <div className="row g-3 mt-1">
              <div className="col-12 col-md-6 col-lg-3 audit-log-filters__field">
                <label className="form-label">User ID</label>
                <SearchBox
                  placeholder="Search by User ID"
                  value={searchId}
                  onSubmit={setSearchId}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-3 audit-log-filters__field">
                <label className="form-label">Employer ID</label>
                <SearchBox
                  placeholder="Search by Employer ID"
                  value={employerId}
                  onSubmit={setEmployerId}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-3 audit-log-filters__field">
                <label className="form-label">Worker ID</label>
                <SearchBox
                  placeholder="Search by Worker ID"
                  value={workerId}
                  onSubmit={setWorkerId}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-3 audit-log-filters__field">
                <label className="form-label">Client Application ID</label>
                <SearchBox
                  placeholder="Search by Client Application ID"
                  value={clientApplicationId}
                  onSubmit={handleClientApplicationSubmit}
                />
              </div>
            </div>
            {/* <div className="row g-3 mt-1">
              <div className="col-12 col-md-6 col-lg-3 audit-log-filters__field">
                <label className="form-label">Client User ID</label>
                <SearchBox
                  placeholder="Search by Client User ID"
                  value={clientUserId}
                  onSubmit={setClientUserId}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-3 audit-log-filters__field">
                <label className="form-label">Client Employer ID</label>
                <SearchBox
                  placeholder="Search by Client Employer ID"
                  value={clientEmployerId}
                  onSubmit={setClientEmployerId}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-3 audit-log-filters__field">
                <label className="form-label">Client Worker ID</label>
                <SearchBox
                  placeholder="Search by Client Worker ID"
                  value={clientWorkerId}
                  onSubmit={setClientWorkerId}
                />
              </div>
            </div> */}
          </div>
        </div>

        <Table loading={isLoading} columns={columns} data={getTableData()} />

        {hasMore && (
          <div className="text-center mt-3">
            <Button
              variant="light-blue"
              onClick={handleLoadMore}
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Load More"}
            </Button>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
